import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import LoadingScreen from "../components/LoadingScreen";
import { showToast } from "../components/Toast";
import { toast } from "react-hot-toast";
import { getGenerationEstimate } from "../utils/audiobookEstimate";
import ConfirmationModal from "../components/ConfirmationModal";
import CharacterSelectGrid from "../components/audiobook/CharacterSelectGrid";
import logger from "../utils/logger";
import { useAnalytics, usePageTracking } from "../hooks/useAnalytics";
import HelpFooter from "../components/HelpFooter";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { saveCreationDraft, takeCreationDraft } from "../utils/creationDraft";

const STEP_LABELS = ["Characters", "Story idea", "Settings", "Title & review"];
const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const READING_LEVELS = [
  { value: 1, label: "Level 1", sub: "Ages 2–4" },
  { value: 2, label: "Level 2", sub: "Ages 4–5" },
  { value: 3, label: "Level 3", sub: "Ages 5–6" },
  { value: 4, label: "Level 4", sub: "Ages 6–7" },
  { value: 5, label: "Level 5", sub: "Ages 7–8" },
];

const STORY_LENGTH_OPTIONS = [
  { value: 8, label: "8 pages", stars: 50 },
  { value: 25, label: "25 pages", stars: 75 },
  { value: 50, label: "50 pages", stars: 100 },
];

const getStoryLengthStarCost = (pageCount) => {
  const matched = STORY_LENGTH_OPTIONS.find((option) => option.value === pageCount);
  return matched?.stars ?? STORY_LENGTH_OPTIONS[0].stars;
};

const AudiobookCreatePage = () => {
  usePageTracking('Audiobook Create');
  const navigate = useNavigate();
  const { trackAudiobookCreated, trackAudiobookCreationStarted } = useAnalytics();
  const { apiClient } = useAPI();
  const { user, costs, updateUser } = useAuth();
  const { requireEntitlement, openWall } = useEntitlementGate();
  // Wall-interrupted draft; restored when checkout returns here.
  const creationDraftRef = useRef(takeCreationDraft("create-storybook"));

  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d) return;
    if (d.storyTitle) setStoryTitle(d.storyTitle);
    if (d.storyDescription) setStoryDescription(d.storyDescription);
    if (typeof d.isPublic === "boolean") setIsPublic(d.isPublic);
    if (d.readingLevel) setReadingLevel(d.readingLevel);
    if (d.selectedPageCount) setSelectedPageCount(d.selectedPageCount);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { currentProfile } = useProfile();

  // Optimized voice recording prompts for Eleven Labs voice cloning
  // Each prompt is designed to be 8-12 seconds when spoken naturally, with good phoneme variety
  const voiceRecordingPrompts = [
    "Hello there. Tonight we're going on a magical adventure together through stories and dreams. Are you ready to begin this journey with me?",
    "Once upon a time, in a land far away, there lived a brave little explorer who loved discovering new places. Every day brought exciting surprises and wonderful friends to meet along the way.",
    "The stars are shining brightly tonight, and the moon is watching over us as we prepare for sleep. Close your eyes and imagine floating among the clouds, feeling peaceful and safe.",
    "Tomorrow is going to be an amazing day filled with laughter, learning, and fun adventures. You'll wake up feeling refreshed and ready to explore everything the world has to offer.",
    "You are so loved and special, and I'm so proud of you. As you drift off to sleep, remember that wonderful dreams are waiting for you tonight."
  ];

  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCharacters, setSelectedCharacters] = useState([]);

  // Characters load async — re-select drafted ones when they arrive.
  // Must sit AFTER characters state is declared (avoids TDZ crash on mount).
  useEffect(() => {
    const d = creationDraftRef.current;
    if (!d?.selectedCharacterIds?.length || !characters.length) return;
    const picked = characters.filter((c) => d.selectedCharacterIds.includes(c.id));
    if (picked.length) setSelectedCharacters(picked);
    creationDraftRef.current = { ...d, selectedCharacterIds: null };
  }, [characters]); // eslint-disable-line react-hooks/exhaustive-deps

  const [storyTitle, setStoryTitle] = useState("");
  const [storyDescription, setStoryDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [readingLevel, setReadingLevel] = useState(1); // Default to level 1
  const [selectedPageCount, setSelectedPageCount] = useState(8);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showMicTooltip, setShowMicTooltip] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [creationStep, setCreationStep] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCreation, setPendingCreation] = useState(false);
  
  // Voice selection state
  const [selectedVoiceOption, setSelectedVoiceOption] = useState("random"); // "parent" | "random"
  const [parentVoiceId, setParentVoiceId] = useState(null);
  const [hasParentVoice, setHasParentVoice] = useState(false);
  const [showRecordVoiceModal, setShowRecordVoiceModal] = useState(false);
  const [recordedFiles, setRecordedFiles] = useState([]);
  const [recordedDurations, setRecordedDurations] = useState([]); // Store duration in seconds for each clip
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [voiceRecorder, setVoiceRecorder] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [isCheckingVoice, setIsCheckingVoice] = useState(false);

  // ── Wizard state ──
  const [currentStep, setCurrentStep] = useState(0);
  const [stepDirection, setStepDirection] = useState(1);
  const [stepError, setStepError] = useState("");

  const validateCurrentStep = () => {
    if (currentStep === 0) {
      if (selectedCharacters.length === 0) return "Pick at least one character to continue.";
      return null;
    }
    if (currentStep === 1) {
      if (!storyDescription.trim() || storyDescription.trim().length < 10) {
        return "Tell us a bit more about your story (at least 10 characters).";
      }
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateCurrentStep();
    if (err) { setStepError(err); return; }
    setStepError("");
    setStepDirection(1);
    setCurrentStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  };

  const goBack = () => {
    setStepError("");
    setStepDirection(-1);
    setCurrentStep((s) => Math.max(0, s - 1));
  };
  const [checklist, setChecklist] = useState({
    clip1: false,
    clip2: false,
    clip3: false,
    clip4: false,
    clip5: false,
  });

  useEffect(() => {
    if (apiClient && user?.id) {
      loadCharacters();
      checkParentVoiceOnLoad();
    }
  }, [apiClient, user?.id]);

  // Check for parent voice on page load
  const checkParentVoiceOnLoad = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setIsCheckingVoice(true);
      const response = await apiClient.getParentVoice();
      
      logger.log("📥 Parent voice check response:", response);
      
      // Backend returns: { "exists": true | false, "parent_voice_id": string | null }
      // Handle both wrapped and unwrapped responses from API client
      const exists = response.exists ?? response.data?.exists ?? (response.status ? response.data?.exists : undefined);
      const parentVoiceIdValue = response.parent_voice_id ?? response.data?.parent_voice_id ?? (response.status ? response.data?.parent_voice_id : undefined);
      
      if (exists === true && parentVoiceIdValue) {
        setParentVoiceId(parentVoiceIdValue);
        setHasParentVoice(true);
        logger.log("✅ Parent voice found on load:", parentVoiceIdValue);
      } else {
        setHasParentVoice(false);
        setParentVoiceId(null);
        logger.log("ℹ️ No parent voice found on load (exists:", exists, ", parent_voice_id:", parentVoiceIdValue, ")");
      }
    } catch (error) {
      logger.error("Error checking parent voice on load:", error);
      setHasParentVoice(false);
      setParentVoiceId(null);
    } finally {
      setIsCheckingVoice(false);
    }
  };

  // Cleanup: Stop recording if component unmounts
  useEffect(() => {
    return () => {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      if (voiceRecorder && voiceRecorder.state !== "inactive") {
        voiceRecorder.stop();
      }
    };
  }, [mediaRecorder, voiceRecorder]);

  // Handle voice option selection - do NOT open modal if parent voice already exists
  useEffect(() => {
    // Don't do anything while checking for parent voice
    if (isCheckingVoice) {
      return;
    }

    if (selectedVoiceOption === "parent") {
      // If parent voice exists, don't open modal
      if (hasParentVoice && parentVoiceId) {
        setShowRecordVoiceModal(false);
        logger.log("✅ Parent voice already exists, not opening modal");
      } else if (!hasParentVoice && !isCheckingVoice) {
        // Only open modal if we confirmed no parent voice exists and we're not checking
        setShowRecordVoiceModal(true);
        logger.log("ℹ️ No parent voice found, opening recording modal");
      }
    } else if (selectedVoiceOption === "random") {
      // Clear modal when switching to random
      setShowRecordVoiceModal(false);
    }
  }, [selectedVoiceOption, hasParentVoice, parentVoiceId, isCheckingVoice]);

  const loadCharacters = async () => {
    if (!apiClient || !user?.id) return;

    try {
      setLoading(true);
      const response = await apiClient.getUserCharacters(user.id, currentProfile?.id);

      if (response.status) {
        setCharacters(response.data || []);
      } else {
        showToast("Failed to load characters", "error");
      }
    } catch (error) {
      logger.error("Error loading characters:", error);
      showToast("Failed to load characters", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStory = async () => {
    if (selectedCharacters.length === 0) {
      showToast("Please select at least one character first", "error");
      return;
    }

    try {
      setIsGeneratingStory(true);
      showToast("Generating audiobook idea...", "info");

      // Send all selected characters + reading level for story generation
      const response = await apiClient.request("/audiobooks/generate-description", {
        method: "POST",
        body: JSON.stringify({ character_ids: selectedCharacters.map(c => c.id), user_id: user?.id, reading_level: readingLevel || 1 }),
      });

      if (response.status && response.data?.description) {
        setStoryDescription(response.data.description);
        showToast("Story idea generated!", "success");
      } else {
        showToast(
          response.error || "Failed to generate story idea",
          "error"
        );
      }
    } catch (error) {
      logger.error("Error generating story:", error);
      showToast("Failed to generate story idea", "error");
    } finally {
      setIsGeneratingStory(false);
    }
  };

  const handleVoiceToText = async () => {
    try {
      // If already recording, stop recording and transcribe
      if (isRecording && mediaRecorder) {
        mediaRecorder.stop();
        setIsRecording(false);
        return;
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach((track) => track.stop());

        if (chunks.length === 0) {
          showToast("⚠️ No audio recorded. Please try again.", "error");
          return;
        }

        // Create blob from audio chunks
        const audioBlob = new Blob(chunks, { type: "audio/webm" });

        const token = apiClient.getAuthToken();

        try {
          setIsTranscribing(true);

          // Step 1: Transcribe audio
          showToast("Transcribing your voice...", "info");

          const formData = new FormData();
          formData.append("audio_file", audioBlob, "recording.webm");

          const transcribeResponse = await fetch(
            `${apiClient.baseURL}/audiobooks/transcribe`,
            {
              method: "POST",
              headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: formData,
            }
          );

          if (!transcribeResponse.ok) {
            throw new Error(`HTTP ${transcribeResponse.status}: ${transcribeResponse.statusText}`);
          }

          const transcribeData = await transcribeResponse.json();

          if (!transcribeData.text) {
            throw new Error("No transcription text in response");
          }

          const transcribedText = transcribeData.text.trim();

          // Step 2: Polish the transcribed text
          showToast("Polishing your story...", "info");

          // Get selected character names (matching Story form pattern)
          const selectedCharacterNames = characters
            .filter(c => selectedCharacters.some(sc => sc.id === c.id))
            .map(c => c.name);

          const polishResponse = await fetch(
            `${apiClient.baseURL}/audiobooks/polish`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: JSON.stringify({ 
                text: transcribedText,
                characters: selectedCharacterNames
              }),
            }
          );

          if (!polishResponse.ok) {
            throw new Error(`HTTP ${polishResponse.status}: ${polishResponse.statusText}`);
          }

          const polishData = await polishResponse.json();

          // Replace story description with polished text (matching Story form pattern)
          if (polishData.text || polishData.polished_text) {
            let polished = polishData.polished_text || polishData.text || "";
            
            // Remove any "Title:" or similar lines if present
            polished = polished.replace(/^Title:.*$/gm, "").trim();
            
            setStoryDescription(polished);
            showToast("Story polished successfully!", "success");
          } else {
            // Fallback: use transcribed text if polish response doesn't have expected format
            setStoryDescription(transcribedText);
            showToast("Voice transcribed successfully!", "success");
          }
        } catch (error) {
          logger.error("Error in voice transcription:", error);
          showToast("Failed to transcribe audio", "error");
        } finally {
          setIsTranscribing(false);
        }
      };

      // Start recording
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      logger.error("❌ Microphone access error:", error);
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        showToast("⚠️ Microphone permission denied. Please allow microphone access.", "error");
      } else {
        showToast("Failed to access microphone", "error");
      }
    }
  };

  // Voice recording handlers
  const handleRecordVoiceClip = async () => {
    try {
      if (isRecordingVoice && voiceRecorder) {
        // Stop recording
        voiceRecorder.stop();
        setIsRecordingVoice(false);
        return;
      }

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      const startTime = Date.now();
      setRecordingStartTime(startTime);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());

        if (chunks.length === 0) {
          showToast("⚠️ No audio recorded. Please try again.", "error");
          setRecordingStartTime(null);
          return;
        }

        // Calculate duration in seconds
        const endTime = Date.now();
        const duration = (endTime - startTime) / 1000; // Convert to seconds
        setRecordingStartTime(null);

        // Validate minimum duration (8 seconds recommended for better quality)
        if (duration < 8) {
          showToast(
            `⚠️ Recording too short (${duration.toFixed(1)}s). Please record for 8–12 seconds for better quality.`,
            "error"
          );
          return;
        }

        const audioBlob = new Blob(chunks, { type: "audio/webm" });
        const audioFile = new File([audioBlob], `voice-clip-${recordedFiles.length + 1}.webm`, {
          type: "audio/webm",
        });

        // Add to recorded files and durations
        const newFiles = [...recordedFiles, audioFile];
        const newDurations = [...recordedDurations, duration];
        setRecordedFiles(newFiles);
        setRecordedDurations(newDurations);

        // Update checklist
        if (newFiles.length === 1) {
          setChecklist({ ...checklist, clip1: true });
        } else if (newFiles.length === 2) {
          setChecklist({ ...checklist, clip2: true });
        } else if (newFiles.length === 3) {
          setChecklist({ ...checklist, clip3: true });
        } else if (newFiles.length === 4) {
          setChecklist({ ...checklist, clip4: true });
        } else if (newFiles.length === 5) {
          setChecklist({ ...checklist, clip5: true });
        }

        showToast(`Clip ${newFiles.length} recorded! (${duration.toFixed(1)}s)`, "success");
      };

      recorder.start(200);
      setVoiceRecorder(recorder);
      setIsRecordingVoice(true);
      showToast(`Recording Step ${recordedFiles.length + 1}... Read the text clearly, then click to stop`, "info");
    } catch (error) {
      logger.error("Error accessing microphone:", error);
      showToast("Failed to access microphone", "error");
      setRecordingStartTime(null);
    }
  };

  const handleDoneRecording = async () => {
    if (recordedFiles.length < 5) {
      showToast("Please record all 5 voice clips to create your voice", "error");
      return;
    }

    // Validate all clips are at least 8 seconds (recommended for better quality)
    const minDuration = 8;
    const shortClips = recordedDurations.filter((duration) => duration < minDuration);
    
    if (shortClips.length > 0) {
      showToast(
        "Each voice clip should be at least 8 seconds long for better quality. Please re-record the short clips.",
        "error"
      );
      return;
    }

    try {
      setIsUploadingVoice(true);
      showToast("Creating your voice...", "info");

      const response = await apiClient.cloneParentVoice(recordedFiles);

      logger.log("📥 Clone parent voice response:", response);

      if (response.status === "success" && response.parent_voice_id) {
        const newParentVoiceId = response.parent_voice_id;
        setParentVoiceId(newParentVoiceId);
        setHasParentVoice(true);
        setShowRecordVoiceModal(false);
        setRecordedFiles([]);
        setRecordedDurations([]);
        setChecklist({ clip1: false, clip2: false, clip3: false, clip4: false, clip5: false });
        showToast("✨ Your voice has been created!", "success");
        logger.log("✅ Parent voice created:", newParentVoiceId);
      } else {
        // Check for voice_sample_too_short error
        const errorMessage = response.error || response.message || "";
        if (errorMessage.toLowerCase().includes("voice_sample_too_short") || 
            errorMessage.toLowerCase().includes("too short")) {
          showToast(
            "Your recording was too short. Please speak for 8–12 seconds per clip for better quality.",
            "error"
          );
        } else {
          const errorMsg = errorMessage ||
            (response.status !== "success" ? `Unexpected status: ${response.status}` : "Missing parent_voice_id") ||
            "Failed to create your voice. Please try again.";
          logger.error("❌ Clone parent voice failed:", { response, errorMsg });
          showToast(errorMsg, "error");
        }
      }
    } catch (error) {
      logger.error("Error creating parent voice:", error);
      const errorMessage = error.message || error.toString() || "";
      if (errorMessage.toLowerCase().includes("voice_sample_too_short") || 
          errorMessage.toLowerCase().includes("too short")) {
        showToast(
          "Your recording was too short. Please speak for 8–12 seconds per clip for better quality.",
          "error"
        );
      } else {
        showToast("Failed to create your voice. Please try again.", "error");
      }
    } finally {
      setIsUploadingVoice(false);
    }
  };

  const handleVoiceOptionChange = (option) => {
    setSelectedVoiceOption(option);
    if (option === "random") {
      // Don't clear parentVoiceId - keep it for future use
      // Just close the modal
      setShowRecordVoiceModal(false);
    } else if (option === "parent") {
      // Only open modal if parent voice doesn't exist
      if (hasParentVoice && parentVoiceId) {
        // Parent voice exists, don't open modal
        setShowRecordVoiceModal(false);
      } else {
        // No parent voice, open recording modal
        setShowRecordVoiceModal(true);
      }
    }
  };

  const getCreateAudiobookErrorMessage = (rawMessage) => {
    const message = String(rawMessage || "").trim();
    if (!message) return "Failed to create audiobook";

    if (message.toLowerCase().includes("page_count")) {
      return "Please choose a valid story length: 8, 25, or 50 pages.";
    }

    if (
      /insufficient\s*stars/i.test(message) ||
      /not\s+enough\s+stars/i.test(message)
    ) {
      return "You do not have enough Pixley Stars for this storybook length.";
    }

    return message;
  };

  const handleCreateAudiobook = async () => {
    console.log("🎧 CREATE AUDIOBOOK - Starting validation checks");
    trackAudiobookCreationStarted({
      characterCount: selectedCharacters.length,
      readingLevel,
      pageCount: selectedPageCount,
      description: storyDescription,
    });

    if (selectedCharacters.length === 0) {
      console.log("❌ ERROR CONDITION HIT: No characters selected");
      showToast("Please select at least one character", "error");
      return;
    }

    if (!storyDescription.trim()) {
      console.log("❌ ERROR CONDITION HIT: No story description provided");
      showToast("Please provide a story description", "error");
      return;
    }

    if (!user?.id) {
      console.log("❌ ERROR CONDITION HIT: User authentication missing", { userId: user?.id });
      showToast("User authentication required", "error");
      return;
    }
    
    console.log("✅ Validation checks passed", {
      charactersCount: selectedCharacters.length,
      descriptionLength: storyDescription.trim().length,
      userId: user?.id
    });

    // Members-only gate first (no-op pre-cutover).
    if (!requireEntitlement({ trigger: "creation", contentType: "storybook" })) {
      saveCreationDraft("create-storybook", {
        selectedCharacterIds: selectedCharacters.map((c) => c.id),
        storyTitle,
        storyDescription,
        isPublic,
        readingLevel,
        selectedPageCount,
      });
      return;
    }

    // Check and deduct stars if required
    const starCost = getStoryLengthStarCost(selectedPageCount);
    if (starCost && starCost > 0) {
      const currentStars = user?.pixley_stars ?? 0;

      if (currentStars < starCost) {
        showToast(
          `You need ${starCost} Pixley Stars to create an audiobook. You have ${currentStars} stars. Visit the Shop to get more stars!`,
          "error"
        );
        return;
      }

      // Show confirmation modal before deducting stars
      setPendingCreation(true);
      setShowConfirmModal(true);
      return;
    }

    // If no star cost, proceed directly with creation
    await proceedWithAudiobookCreation();
  };

  const proceedWithAudiobookCreation = async () => {
    if (selectedCharacters.length === 0 || !storyDescription.trim() || !user?.id) {
      return;
    }

    try {
      setIsCreating(true);
      setShowConfirmModal(false);

      // No pre-deduction here: the backend deducts stars inside /audiobooks/create
      // and returns the new balance (remaining_stars), applied after creation.

      setCreationStep("Creating your audiobook...");

      const toastId = toast.loading("Creating your audiobook...");

      // Prepare FormData
      // Note: Character visuals are generated per page independently. Consistency is not enforced intentionally.
      const formData = new FormData();
      formData.append("user_id", user.id);
      if (currentProfile?.id) {
        formData.append("profile_id", currentProfile.id);
      }
      // Send character IDs as comma-separated string
      const characterIds = selectedCharacters.map(c => c.id).join(',');
      formData.append("character_ids", characterIds);
      formData.append("title", storyTitle.trim() || ""); // Empty for AI generation
      formData.append("description", storyDescription.trim());
      formData.append("is_public", isPublic);
      formData.append("page_count", String(selectedPageCount));
      
      // Add reading_level (always send, default to 1 if not selected)
      const finalReadingLevel = readingLevel || 1;
      formData.append("reading_level", finalReadingLevel);
      logger.log("📤 Reading level:", finalReadingLevel);
      
      // Always send voice_source (either "random" or "parent")
      formData.append("voice_source", selectedVoiceOption);
      logger.log("📤 Voice source:", selectedVoiceOption);
      
      // Add parent_voice_id ONLY if voice_source is "parent" AND voice exists
      // When voice_source is "random", parent_voice_id is NOT sent
      if (selectedVoiceOption === "parent" && parentVoiceId) {
        formData.append("parent_voice_id", parentVoiceId);
        logger.log("📤 Including parent_voice_id:", parentVoiceId);
      } else if (selectedVoiceOption === "parent" && !parentVoiceId) {
        logger.warn("⚠️ Parent voice selected but no parent_voice_id available");
      } else if (selectedVoiceOption === "random") {
        // Explicitly ensure parent_voice_id is NOT sent when using random voice
        logger.log("📤 Using random voice - parent_voice_id will NOT be sent");
      }

      // Add character image (optional reference)
      // Note: Character visuals are generated per page independently. Consistency is not enforced intentionally.
      // Backend may use this image as a reference, but each page is generated separately.
      if (selectedCharacters.length > 0) {
        const firstCharacter = selectedCharacters[0];
        const characterImageUrl = firstCharacter.animated_image_url || firstCharacter.handdrawn_image_url || firstCharacter.image_url;
        
        if (characterImageUrl) {
          try {
            logger.log("🖼️ Fetching character image:", {
              characterName: firstCharacter.name,
              characterId: firstCharacter.id,
              imageUrl: characterImageUrl,
              imageType: firstCharacter.animated_image_url ? 'animated' : 'handdrawn'
            });
            
            // Fetch the character image as blob
            const imageResponse = await fetch(characterImageUrl);
            if (!imageResponse.ok) {
              throw new Error(`HTTP ${imageResponse.status}: Failed to fetch character image`);
            }
            
            const imageBlob = await imageResponse.blob();
            
            // Append character image to FormData (optional reference)
            formData.append("character_image", imageBlob, `${firstCharacter.name || 'character'}.png`);
            
            logger.log("✅ Character image appended to FormData:", {
              characterName: firstCharacter.name,
              imageSize: imageBlob.size,
              imageType: imageBlob.type
            });
          } catch (error) {
            logger.error("❌ Failed to fetch character image:", error);
            // Continue without image - backend handles gracefully
            logger.warn("⚠️ Continuing without character image");
          }
        }
      }

      // Log FormData payload
      const formDataEntries = Array.from(formData.entries());
      console.log("🎧 CREATE AUDIOBOOK - API Request Payload:", {
        user_id: user.id,
        character_ids: characterIds,
        character_count: selectedCharacters.length,
        title: storyTitle.trim() || "(AI Generated)",
        description: storyDescription.trim().substring(0, 50) + "...",
        is_public: isPublic,
        page_count: selectedPageCount,
        reading_level: finalReadingLevel,
        formDataEntries: formDataEntries.map(([key, value]) => ({
          key,
          value: typeof value === 'string' ? value : `[${typeof value}]`
        }))
      });
      
      logger.log("📤 Creating audiobook with data:", {
        user_id: user.id,
        character_ids: characterIds,
        character_count: selectedCharacters.length,
        title: storyTitle.trim() || "(AI Generated)",
        description: storyDescription.trim().substring(0, 50) + "...",
        is_public: isPublic,
        page_count: selectedPageCount,
        reading_level: finalReadingLevel,
        voice_source: selectedVoiceOption,
        hasParentVoiceId: formData.has("parent_voice_id"),
        parentVoiceId: selectedVoiceOption === "parent" && parentVoiceId ? parentVoiceId : null,
        hasCharacterImage: formData.has("character_image"),
        characterImageSource: selectedCharacters[0]?.animated_image_url ? 'animated' : selectedCharacters[0]?.handdrawn_image_url ? 'handdrawn' : 'none'
      });

      let response;
      try {
        response = await apiClient.createAudiobook(formData);
      } catch (networkError) {
        console.log("❌ ERROR CONDITION HIT: Network error during API call", {
          error: networkError,
          errorType: 'network',
          errorMessage: networkError?.message,
          stack: networkError?.stack
        });
        throw networkError;
      }

      toast.dismiss(toastId);

      console.log("🎧 CREATE AUDIOBOOK RESPONSE:", {
        fullResponse: response,
        responseType: typeof response,
        hasStatus: 'status' in response,
        hasData: 'data' in response,
        hasAudiobookId: 'audiobook_id' in response,
        hasDataAudiobookId: response?.data && 'audiobook_id' in response.data,
        hasStoryId: 'story_id' in response,
        hasId: 'id' in response,
        responseKeys: response ? Object.keys(response) : [],
        status: response?.status,
        data: response?.data,
        error: response?.error,
        message: response?.message
      });
      
      logger.log("📥 Backend response:", response);

      // Check for audiobook_id (not story_id for audiobooks)
      console.log("🎧 CREATE AUDIOBOOK - Checking for audiobook ID", {
        expectations: {
          path1: 'response?.data?.audiobook_id',
          path2: 'response?.audiobook_id',
          path3: 'response?.id'
        },
        actualValues: {
          dataAudiobookId: response?.data?.audiobook_id,
          audiobookId: response?.audiobook_id,
          id: response?.id,
          dataKeys: response?.data ? Object.keys(response.data) : []
        }
      });
      
      const audiobookId =
        response?.data?.audiobook_id ||
        response?.audiobook_id ||
        response?.id;
      const responsePageCount =
        response?.data?.page_count ?? response?.page_count ?? selectedPageCount;

      if (audiobookId) {
        console.log("✅ SUCCESS PATH HIT: Audiobook ID found", {
          audiobookId,
          foundInPath: 
            response?.data?.audiobook_id ? 'response.data.audiobook_id' :
            response?.audiobook_id ? 'response.audiobook_id' :
            response?.id ? 'response.id' : 'unknown'
        });
        console.log("✅ AUDIOBOOK CREATED:", audiobookId);
        logger.log("✅ Audiobook creation started with ID:", audiobookId);
        logger.log("✅ Audiobook create response page_count:", responsePageCount);
        trackAudiobookCreated({
          title: storyTitle.trim() || "(AI Generated)",
          characterCount: selectedCharacters.length,
          voiceOption: selectedVoiceOption,
          readingLevel: finalReadingLevel,
          pageCount: responsePageCount,
        });
        // storybook_created fires server-side when pipeline completes (no client-side to avoid double-counting)

        // Backend deducted stars in /audiobooks/create; apply the returned balance.
        const remainingStars = response?.remaining_stars ?? response?.data?.remaining_stars;
        if (remainingStars !== undefined) {
          updateUser({ ...user, pixley_stars: remainingStars });
        }

        // Add CREATEAUDIOBOOK tag to user's data_tags
        const tag = `CREATEAUDIOBOOK_${audiobookId}`;
        apiClient.addUserDataTag(user.id, tag).then((result) => {
          if (result.status) {
            logger.log(`✅ Added tag ${tag} for user ${user.id}`);
          } else {
            logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
          }
        }).catch((error) => {
          logger.error(`❌ Error adding tag ${tag}:`, error);
        });

        // Show notification that story will be ready soon
        toast.success(`Your storybook will be ready in ${getGenerationEstimate(selectedPageCount)}! We'll notify you when it's ready.`, {
          duration: 8000,
          position: "top-right",
        });

        // Reset form
        setSelectedCharacters([]);
        setStoryTitle("");
        setStoryDescription("");
        setIsPublic(false);
        setPendingCreation(false);
        setSelectedVoiceOption("random");
        setReadingLevel(1); // Reset to default
        setSelectedPageCount(8);
        setParentVoiceId(null);

        // Navigate to storyverse after short delay when audiobook is successfully created
        console.log("🎧 CREATE AUDIOBOOK - Redirecting to storyverse page");
        setTimeout(() => {
          navigate("/storyverse");
        }, 500);
      } else {
        console.log("❌ ERROR CONDITION HIT: No audiobook ID found in response", {
          reason: 'audiobookId is falsy',
          audiobookId: audiobookId,
          response: response,
          checkedPaths: {
            'response?.data?.audiobook_id': response?.data?.audiobook_id,
            'response?.audiobook_id': response?.audiobook_id,
            'response?.id': response?.id
          },
          responseStructure: {
            hasData: !!response?.data,
            hasStatus: 'status' in response,
            statusValue: response?.status,
            hasError: 'error' in response,
            hasMessage: 'message' in response,
            topLevelKeys: response ? Object.keys(response) : []
          }
        });
        
        const backendMessage =
          response?.error ||
          response?.message ||
          response?.data?.error ||
          response?.data?.message ||
          "Failed to create story";
        const errorMessage = getCreateAudiobookErrorMessage(backendMessage);
        console.log("🎧 CREATE AUDIOBOOK - Showing error toast", {
          errorMessage,
          source: response?.error
            ? "response.error"
            : response?.message
            ? "response.message"
            : response?.data?.error
            ? "response.data.error"
            : response?.data?.message
            ? "response.data.message"
            : "fallback",
        });
        showToast(errorMessage, "error");
        setPendingCreation(false);
        logger.error("❌ Story creation failed:", response);
      }
    } catch (error) {
      logger.error("Error creating audiobook:", error);
      const errorMessage = getCreateAudiobookErrorMessage(error?.message);
      showToast(errorMessage, "error");
      setPendingCreation(false);
    } finally {
      setIsCreating(false);
      setCreationStep("");
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading your library..." type="character" />;
  }

  // ─────────────────── Wizard step renderers ───────────────────
  const handleCharacterToggle = (character) => {
    setSelectedCharacters((prev) => {
      const exists = prev.find((c) => c.id === character.id);
      if (exists) return prev.filter((c) => c.id !== character.id);
      if (prev.length >= 3) {
        showToast("You can select up to 3 characters", "info");
        return prev;
      }
      return [...prev, character];
    });
  };

  const renderStepCharacters = () => (
    <CharacterSelectGrid
      characters={characters}
      selectedCharacters={selectedCharacters}
      onToggle={handleCharacterToggle}
      maxSelected={3}
    />
  );

  const renderStepIdea = () => (
    <div>
      <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, letterSpacing: "-0.01em", textTransform: "uppercase", margin: "0 0 4px" }}>
        Your story idea
      </h2>
      <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 16px" }}>
        What would you like your storybook to be about?
      </p>

      <div
        style={{
          background: "#FFFFFF",
          border: "2px solid rgba(11,26,51,0.08)",
          borderRadius: 18,
          boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
          overflow: "hidden",
        }}
      >
        <textarea
          value={storyDescription}
          onChange={(e) => setStoryDescription(e.target.value)}
          maxLength={7000}
          rows={6}
          placeholder="Tell us what you'd like to read about — a setting, a problem, a fun twist…"
          className="w-full p-5 bg-transparent focus:outline-none resize-none"
          style={{ ...FREDOKA, color: "#0B1A33", fontSize: 16, lineHeight: 1.5 }}
        />
        <div className="flex items-center justify-between gap-2 px-3 pb-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleVoiceToText}
              disabled={isTranscribing}
              className="inline-flex items-center gap-2 px-3.5 py-2 transition-all disabled:opacity-60"
              style={{
                ...FREDOKA,
                background: isRecording ? "#FF6B9A" : "#FFFFFF",
                color: isRecording ? "#FFFFFF" : "#334155",
                fontWeight: 700,
                fontSize: 13,
                border: isRecording ? "none" : "2px solid rgba(11,26,51,0.08)",
                borderRadius: 999,
                boxShadow: isRecording ? "0px 2px 0px #C94D73" : "0px 2px 0px rgba(11,26,51,0.04)",
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
              </svg>
              {isRecording ? "Stop recording" : isTranscribing ? "Transcribing…" : "Voice"}
            </button>
            <button
              type="button"
              onClick={handleGenerateStory}
              disabled={isGeneratingStory || selectedCharacters.length === 0}
              className="inline-flex items-center gap-2 px-3.5 py-2 transition-all disabled:opacity-60"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                border: "2px solid rgba(0,101,248,0.22)",
                borderRadius: 999,
                boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l1.6 5.8L19.5 9.5l-5.9 1.7L12 17l-1.6-5.8L4.5 9.5l5.9-1.7L12 2z" />
              </svg>
              {isGeneratingStory ? "Thinking…" : "Surprise me"}
            </button>
          </div>
          <span style={{ ...FREDOKA, color: "#8CA0BC", fontSize: 12 }}>
            {storyDescription.length}/7000
          </span>
        </div>
      </div>
    </div>
  );

  const renderStepSettings = () => {
    const pillBase = {
      ...FREDOKA,
      background: "#FFFFFF",
      color: "#334155",
      fontWeight: 700,
      fontSize: 15,
      border: "2px solid rgba(11,26,51,0.08)",
      borderRadius: 16,
      boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
      cursor: "pointer",
      padding: "14px 22px",
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      minWidth: 110,
    };
    const pillActive = {
      ...pillBase,
      background: "#0065F8",
      color: "#FFFFFF",
      border: "2px solid #0065F8",
      boxShadow: "0px 3px 0px #073B8E",
    };
    const fieldLabelStyle = { ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 10px", textAlign: "center" };

    return (
      <div>
        <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, letterSpacing: "-0.01em", textTransform: "uppercase", margin: "0 0 4px" }}>
          Settings
        </h2>
        <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 20px" }}>
          Story length, reading level, voice, and visibility for your storybook.
        </p>

        {/* Story Length */}
        <div className="mb-6">
          <p style={fieldLabelStyle}>Story length</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {STORY_LENGTH_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelectedPageCount(opt.value)}
                style={selectedPageCount === opt.value ? pillActive : pillBase}
              >
                <span>{opt.label}</span>
                <span style={{ fontSize: 12, opacity: 0.75 }}>
                  {opt.stars} stars
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Reading Level */}
        <div className="mb-6">
          <p style={fieldLabelStyle}>Reading level</p>
          <div className="flex flex-wrap gap-3 justify-center">
            {READING_LEVELS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setReadingLevel(opt.value)}
                style={readingLevel === opt.value ? pillActive : pillBase}
              >
                <span>{opt.label}</span>
                <span style={{ fontSize: 12, opacity: 0.75 }}>{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Voice */}
        <div className="mb-6">
          <p style={fieldLabelStyle}>Narrator voice</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => setSelectedVoiceOption("random")}
              style={selectedVoiceOption === "random" ? pillActive : pillBase}
            >
              <span>✨ Random voice</span>
              <span style={{ fontSize: 12, opacity: 0.75 }}>A surprise reader</span>
            </button>
            <button
              type="button"
              onClick={() => setSelectedVoiceOption("parent")}
              style={selectedVoiceOption === "parent" ? pillActive : pillBase}
            >
              <span>🎙️ My voice</span>
              <span style={{ fontSize: 12, opacity: 0.75 }}>
                {hasParentVoice ? "Use my recorded voice" : "Record your voice"}
              </span>
            </button>
          </div>
        </div>

        {/* Visibility */}
        <div>
          <p style={fieldLabelStyle}>Visibility</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={() => setIsPublic(false)}
              style={!isPublic ? pillActive : pillBase}
            >
              <span>🔒 Private</span>
              <span style={{ fontSize: 12, opacity: 0.75 }}>Only you can see it</span>
            </button>
            <button
              type="button"
              onClick={() => setIsPublic(true)}
              style={isPublic ? pillActive : pillBase}
            >
              <span>🌍 Public</span>
              <span style={{ fontSize: 12, opacity: 0.75 }}>Featured on Storyverse</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStepReview = () => {
    const levelInfo = READING_LEVELS.find((l) => l.value === readingLevel) || READING_LEVELS[0];
    const lengthInfo =
      STORY_LENGTH_OPTIONS.find((option) => option.value === selectedPageCount) ||
      STORY_LENGTH_OPTIONS[0];
    const chips = [
      `${lengthInfo.label} · ${lengthInfo.stars} stars`,
      `${levelInfo.label} · ${levelInfo.sub}`,
      selectedVoiceOption === "parent" ? "Your voice" : "Random voice",
      isPublic ? "Public" : "Private",
    ];

    return (
      <div>
        <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, letterSpacing: "-0.01em", textTransform: "uppercase", margin: "0 0 4px" }}>
          Title & review
        </h2>
        <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 16px" }}>
          Give your storybook a title (or let us pick one), then create.
        </p>

        <label style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>
          Storybook title <span style={{ color: "#8CA0BC", letterSpacing: 0, textTransform: "none", fontWeight: 600, fontSize: 11 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={storyTitle}
          onChange={(e) => setStoryTitle(e.target.value)}
          placeholder="Leave blank for AI-generated title"
          maxLength={100}
          className="w-full p-3 focus:outline-none transition-all"
          style={{
            ...FREDOKA,
            background: "#FFFFFF",
            color: "#0B1A33",
            fontSize: 15,
            border: "2px solid rgba(11,26,51,0.08)",
            borderRadius: 14,
            boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
          }}
        />

        {/* Recap card */}
        <div
          className="mt-5 p-4"
          style={{
            background: "linear-gradient(180deg, #F5FAFF 0%, #FFFFFF 100%)",
            border: "2px solid rgba(0,101,248,0.18)",
            borderRadius: 18,
            boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
          }}
        >
          <p style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", margin: "0 0 10px" }}>
            Recap
          </p>

          <div className="flex items-center gap-3 mb-3">
            <div className="flex -space-x-3">
              {selectedCharacters.slice(0, 5).map((c) => (
                <img
                  key={c.id}
                  src={c.animated_image_url || c.handdrawn_image_url || c.image_url || "/assets/handdrawn-character.png"}
                  alt={c.name}
                  className="w-10 h-10 rounded-full object-cover"
                  style={{ border: "2px solid #FFFFFF", boxShadow: "0px 1px 0px rgba(11,26,51,0.06)" }}
                />
              ))}
            </div>
            <span style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 600, fontSize: 14 }}>
              {selectedCharacters.map((c) => c.name).join(", ")}
            </span>
          </div>

          <p
            style={{
              ...FREDOKA,
              color: "#334155",
              fontSize: 13,
              lineHeight: 1.5,
              margin: "0 0 10px",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {storyDescription || <em style={{ color: "#8CA0BC" }}>No prompt yet</em>}
          </p>

          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <span
                key={chip}
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 11,
                  border: "2px solid rgba(0,101,248,0.18)",
                  borderRadius: 999,
                  padding: "3px 10px",
                  letterSpacing: "0.04em",
                }}
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -180, left: -160, width: 480, height: 480, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -200, right: -200, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            type="button"
            onClick={() => navigate("/audiobooks")}
            className="inline-flex items-center gap-2 mb-3 transition-colors"
            style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, fontWeight: 600 }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Storybooks
          </button>
          <h1
            style={{
              ...FREDOKA,
              color: "#0B1A33",
              fontWeight: 700,
              fontSize: "clamp(1.75rem, 3.6vw, 2.6rem)",
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              lineHeight: 1.05,
              margin: "0 0 4px",
            }}
          >
            Create <span style={{ color: "#0065F8" }}>Storybook</span>
          </h1>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 15, margin: 0 }}>
            Step {currentStep + 1} of {STEP_LABELS.length} · {STEP_LABELS[currentStep]}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6 sm:mb-8">
          {STEP_LABELS.map((label, i) => {
            const isActive = i === currentStep;
            const isDone = i < currentStep;
            return (
              <React.Fragment key={label}>
                <button
                  type="button"
                  onClick={() => { if (i < currentStep) { setStepDirection(-1); setCurrentStep(i); setStepError(""); } }}
                  className="flex items-center gap-2 transition-all"
                  style={{
                    ...FREDOKA,
                    height: 32,
                    padding: isActive ? "0 14px" : "0 10px",
                    borderRadius: 999,
                    background: isActive ? "#0065F8" : isDone ? "rgba(0,101,248,0.15)" : "rgba(11,26,51,0.06)",
                    color: isActive ? "#FFFFFF" : isDone ? "#0065F8" : "#5A6B85",
                    border: "none",
                    boxShadow: isActive ? "0px 3px 0px #073B8E" : "none",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    cursor: i <= currentStep ? "pointer" : "default",
                    whiteSpace: "nowrap",
                  }}
                >
                  {isDone && (
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">{label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
                {i < STEP_LABELS.length - 1 && (
                  <div style={{ flex: 1, height: 2, background: i < currentStep ? "rgba(0,101,248,0.35)" : "rgba(11,26,51,0.10)", borderRadius: 999 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step Card */}
        <form onSubmit={(e) => { e.preventDefault(); handleCreateAudiobook(); }}>
          <div
            className="relative overflow-hidden"
            style={{
              minHeight: 460,
              background: "#FFFFFF",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 28,
              boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)",
              padding: "24px 24px 28px",
            }}
          >
            <AnimatePresence mode="wait" custom={stepDirection} initial={false}>
              <motion.div
                key={currentStep}
                custom={stepDirection}
                initial={(d) => ({ x: d * 40, opacity: 0 })}
                animate={{ x: 0, opacity: 1 }}
                exit={(d) => ({ x: d * -40, opacity: 0 })}
                transition={{ type: "spring", stiffness: 300, damping: 28 }}
              >
                {currentStep === 0 && renderStepCharacters()}
                {currentStep === 1 && renderStepIdea()}
                {currentStep === 2 && renderStepSettings()}
                {currentStep === 3 && renderStepReview()}
              </motion.div>
            </AnimatePresence>
          </div>

          {stepError && (
            <div
              className="mt-3 px-4 py-2.5"
              style={{
                ...FREDOKA,
                background: "rgba(220,38,38,0.08)",
                color: "#B91C1C",
                border: "2px solid rgba(220,38,38,0.25)",
                borderRadius: 14,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {stepError}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-6 gap-3">
            {currentStep > 0 ? (
              <button
                key="wizard-back"
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-2 px-5 py-3 transition-all active:scale-[0.98]"
                style={{
                  ...FREDOKA,
                  background: "#FFFFFF",
                  color: "#0065F8",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "2px solid rgba(0,101,248,0.22)",
                  borderRadius: 999,
                  boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                  cursor: "pointer",
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <span />
            )}

            {currentStep < STEP_LABELS.length - 1 ? (
              <button
                key="wizard-continue"
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98]"
                style={{
                  ...FREDOKA,
                  background: "#0065F8",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 999,
                  boxShadow: "0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.20)",
                  cursor: "pointer",
                }}
              >
                Continue
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                key="wizard-create"
                type="submit"
                disabled={!storyDescription.trim() || selectedCharacters.length === 0 || isCreating}
                className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  ...FREDOKA,
                  background: "#0065F8",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 999,
                  boxShadow: "0px 4px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.22)",
                  cursor: "pointer",
                }}
              >
                {isCreating ? "Creating…" : "Create Storybook ✨"}
                {!isCreating && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 ml-1"
                    style={{
                      background: "rgba(255,255,255,0.22)",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {getStoryLengthStarCost(selectedPageCount)}
                    <img src="/assets/pixley_stars.png" alt="" style={{ width: 12, height: 12, filter: "brightness(0) invert(1)" }} />
                  </span>
                )}
              </button>
            )}
          </div>

          {currentStep === STEP_LABELS.length - 1 && (
            <p className="text-center mt-3" style={{ ...FREDOKA, color: "#8CA0BC", fontSize: 12 }}>
              Storybook generation typically takes {getGenerationEstimate(selectedPageCount)}. We'll notify you when it's ready ✨
            </p>
          )}
        </form>
      </div>
      {/* Confirmation Modal for Star Deduction */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={proceedWithAudiobookCreation}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingCreation(false);
        }}
        title="Confirm Storybook Creation"
        starCost={getStoryLengthStarCost(selectedPageCount)}
        currentStars={user?.pixley_stars ?? 0}
        actionType="audiobook"
        confirmText="Create Storybook"
        cancelText="Cancel"
        confirmColor="purple"
      />

      {/* Record Voice Modal */}
      {showRecordVoiceModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          style={{
            background: "rgba(11,26,51,0.42)",
            backdropFilter: "blur(8px)",
            fontFamily: "'Inter', 'Fredoka', sans-serif",
          }}
        >
          <div
            className="relative w-full max-w-2xl flex flex-col my-8 overflow-hidden"
            style={{
              background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
              border: "2px solid rgba(11,26,51,0.06)",
              borderRadius: 28,
              boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
              maxHeight: "calc(100vh - 4rem)",
            }}
          >
            {/* Decorative blob */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: -70,
                right: -70,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background: "rgba(0,101,248,0.08)",
                filter: "blur(20px)",
                pointerEvents: "none",
              }}
            />

            {/* Header */}
            <div className="relative flex-shrink-0 p-6 pb-4">
              <button
                onClick={() => {
                  setShowRecordVoiceModal(false);
                  setRecordedFiles([]);
                  setRecordedDurations([]);
                  setChecklist({ clip1: false, clip2: false, clip3: false, clip4: false, clip5: false });
                  if (!hasParentVoice) {
                    setSelectedVoiceOption("random");
                    setParentVoiceId(null);
                  }
                }}
                aria-label="Close"
                style={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  width: 36,
                  height: 36,
                  background: "#FFFFFF",
                  color: "#5A6B85",
                  border: "2px solid rgba(11,26,51,0.08)",
                  borderRadius: 12,
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.06)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <span
                style={{
                  ...FREDOKA,
                  color: "#0065F8",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Voice Setup
              </span>
              <h2
                className="mt-1"
                style={{
                  ...FREDOKA,
                  color: "#0B1A33",
                  fontWeight: 700,
                  fontSize: "clamp(1.5rem, 2.6vw, 2rem)",
                  letterSpacing: "-0.01em",
                  textTransform: "uppercase",
                  lineHeight: 1.05,
                  margin: 0,
                }}
              >
                Record Your Voice
              </h2>
              <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "6px 0 16px" }}>
                Read 5 short prompts so we can clone your voice for your storybooks.
              </p>

              {/* Step indicator dots */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {[0, 1, 2, 3, 4].map((i) => {
                    const isCurrent = i === recordedFiles.length;
                    const isDone = i < recordedFiles.length;
                    return (
                      <span
                        key={i}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: isCurrent ? 30 : 22,
                          height: 22,
                          borderRadius: isCurrent ? 999 : "50%",
                          background: isDone || isCurrent ? "#0065F8" : "rgba(11,26,51,0.10)",
                          color: isDone || isCurrent ? "#FFFFFF" : "#5A6B85",
                          fontFamily: "'Fredoka', sans-serif",
                          fontWeight: 800,
                          fontSize: 11,
                          boxShadow: isCurrent ? "0px 2px 0px #073B8E" : "none",
                          transition: "all 200ms ease",
                        }}
                      >
                        {isDone ? (
                          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          i + 1
                        )}
                      </span>
                    );
                  })}
                </div>
                <span style={{ ...FREDOKA, color: "#5A6B85", fontWeight: 700, fontSize: 12 }}>
                  {recordedFiles.length}/5 done
                </span>
              </div>

              {/* Progress bar */}
              <div
                className="mt-3 overflow-hidden"
                style={{
                  background: "rgba(11,26,51,0.08)",
                  borderRadius: 999,
                  height: 6,
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${(recordedFiles.length / 5) * 100}%`,
                    background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
                    borderRadius: 999,
                    transition: "width 320ms ease",
                  }}
                />
              </div>
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-y-auto px-6 pb-4 min-h-0">
              {/* Text to read */}
              <div
                className="p-5 mb-4"
                style={{
                  background: "#FFFFFF",
                  border: "2px solid rgba(0,101,248,0.18)",
                  borderRadius: 18,
                  boxShadow: "0px 2px 0px rgba(11,26,51,0.04), 0px 6px 16px rgba(11,26,51,0.06)",
                }}
              >
                <p
                  className="text-center"
                  style={{
                    ...FREDOKA,
                    color: "#0065F8",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    margin: "0 0 12px",
                  }}
                >
                  Read this aloud
                </p>
                <p
                  className="text-center"
                  style={{
                    ...FREDOKA,
                    color: "#0B1A33",
                    fontSize: 17,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    margin: "0 0 10px",
                  }}
                >
                  "{voiceRecordingPrompts[recordedFiles.length] || voiceRecordingPrompts[voiceRecordingPrompts.length - 1]}"
                </p>
                <p
                  className="text-center"
                  style={{
                    ...FREDOKA,
                    color: "#5A6B85",
                    fontSize: 12,
                    margin: 0,
                  }}
                >
                  Aim for <strong style={{ color: "#0B1A33" }}>8–12 seconds</strong> at a natural pace.
                </p>
              </div>

              {/* Record button */}
              <button
                onClick={handleRecordVoiceClip}
                disabled={isUploadingVoice || recordedFiles.length >= 5}
                className="w-full transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  ...FREDOKA,
                  background: isRecordingVoice
                    ? "#FF6B9A"
                    : recordedFiles.length >= 5
                      ? "rgba(11,26,51,0.08)"
                      : "#0065F8",
                  color: isRecordingVoice
                    ? "#FFFFFF"
                    : recordedFiles.length >= 5
                      ? "#5A6B85"
                      : "#FFFFFF",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  borderRadius: 999,
                  padding: "14px 22px",
                  boxShadow: isRecordingVoice
                    ? "0px 4px 0px #C94D73, 0px 12px 28px rgba(255,107,154,0.30)"
                    : recordedFiles.length >= 5
                      ? "none"
                      : "0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.20)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                }}
              >
                {isRecordingVoice ? (
                  <>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: "#FFFFFF",
                        boxShadow: "0 0 0 4px rgba(255,255,255,0.35)",
                        animation: "rv-pulse 1s ease-in-out infinite",
                      }}
                    />
                    <span>Recording… tap to stop</span>
                  </>
                ) : recordedFiles.length >= 5 ? (
                  <>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    All five recordings done
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
                    </svg>
                    {recordedFiles.length === 0
                      ? "Start recording — clip 1"
                      : `Start recording — clip ${recordedFiles.length + 1}`}
                  </>
                )}
              </button>

              {/* Live timer */}
              {isRecordingVoice && recordingStartTime && (
                <p
                  className="text-center mt-2"
                  style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12 }}
                >
                  Recording for{" "}
                  <strong style={{ color: "#0B1A33" }}>
                    {Math.floor((Date.now() - recordingStartTime) / 1000)}s
                  </strong>
                </p>
              )}

              {/* Encouragement */}
              {recordedFiles.length > 0 && recordedFiles.length < 5 && !isRecordingVoice && (
                <div
                  className="mt-4 p-3"
                  style={{
                    background: "#FFFFFF",
                    border: "2px solid rgba(16,185,129,0.30)",
                    borderRadius: 14,
                    boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
                  }}
                >
                  <p
                    style={{
                      ...FREDOKA,
                      color: "#047857",
                      fontWeight: 700,
                      fontSize: 13,
                      margin: 0,
                    }}
                  >
                    Nice! {recordedFiles.length} of 5 clips saved — {5 - recordedFiles.length} to go.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="flex-shrink-0 p-5 pt-4"
              style={{
                background: "rgba(245,250,255,0.85)",
                borderTop: "2px solid rgba(11,26,51,0.06)",
              }}
            >
              {recordedFiles.length < 5 ? (
                <div className="text-center">
                  <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, margin: "0 0 8px" }}>
                    Finish all 5 clips to create your voice
                  </p>
                  <button
                    disabled
                    className="px-6 py-2.5"
                    style={{
                      ...FREDOKA,
                      background: "rgba(11,26,51,0.08)",
                      color: "#8CA0BC",
                      fontWeight: 700,
                      fontSize: 13,
                      border: "none",
                      borderRadius: 999,
                      cursor: "not-allowed",
                    }}
                  >
                    Complete all clips first
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <p style={{ ...FREDOKA, color: "#047857", fontWeight: 700, fontSize: 13, margin: "0 0 10px" }}>
                    All 5 clips saved — ready to clone your voice!
                  </p>
                  <button
                    onClick={handleDoneRecording}
                    disabled={isUploadingVoice}
                    className="transition-all active:scale-[0.98]"
                    style={{
                      ...FREDOKA,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 15,
                      border: "none",
                      borderRadius: 999,
                      padding: "12px 28px",
                      boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                      cursor: isUploadingVoice ? "wait" : "pointer",
                      opacity: isUploadingVoice ? 0.7 : 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {isUploadingVoice ? (
                      <>
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            border: "2px solid rgba(255,255,255,0.35)",
                            borderTopColor: "#FFFFFF",
                            animation: "rv-spin 1s linear infinite",
                          }}
                        />
                        Creating your voice…
                      </>
                    ) : (
                      <>
                        Create my voice ✨
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            <style>{`
              @keyframes rv-pulse {
                0%, 100% { transform: scale(1);   opacity: 1; }
                50%      { transform: scale(1.4); opacity: 0.6; }
              }
              @keyframes rv-spin { to { transform: rotate(360deg); } }
            `}</style>
          </div>
        </div>
      )}
      <HelpFooter />
    </div>
  );
};

export default AudiobookCreatePage;